import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { type CommonError } from 'src/api/base'
import Statements from 'src/assets/icons/customIcons/page-icons/Statements'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import { FormSubmit } from 'src/views/components/forms/formik/FormSubmit'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Loader from 'src/views/components/Loader'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import { templateName } from './templateName'

interface TemplateValues {
  readonly title: string
  readonly titleEn: string
  readonly status: string
}

export default function AdministratorStatementTemplatePage(): JSX.Element {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('statement:statement_templates')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const navigate = useNavigate()
  const headers = useAuthenticatedHeaders()
  const { id } = useParams() as { readonly id: string }
  const listPath = `/${locale}/administrator/general/statement-templates`

  const { data: template, error } = useApi({
    endpoint: Api.getAdministratorStatementTemplate,
    params: React.useMemo(() => ({ headers, args: { id } }), [headers, id]),
    suspense: false,
  })

  React.useEffect(() => {
    if (error != null) {
      toast.error(errorMessage(error as unknown as CommonError))
      navigate(listPath, { replace: true })
    }
  }, [error, navigate, listPath])

  const title = template != null ? templateName(template, locale) : t('statement:statement_templates')
  const breadcrumbsItems = [
    { page: `${t('system:general_information')} - ${t('statement:statement_templates')}`, path: listPath },
    { page: template != null ? templateName(template, locale) : undefined, path: `${listPath}/${id}` },
  ]

  return (
    <Section title={title} breadcrubms={breadcrumbsItems} icon={<Statements />}>
      {template == null ? <Loader className="m-auto my-10 flex" /> : <TemplateForm template={template} />}
    </Section>
  )
}

function TemplateForm({ template }: { readonly template: Api.AdministratorStatementTemplate }): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const navigate = useNavigate()
  const headers = useAuthenticatedHeaders()
  // Faculty administrators and viewers see the form read-only; the backend answers them 403
  const canManage = useAdminAccess().canManage('statementTemplates')
  const listPath = `/${locale}/administrator/general/statement-templates`

  const initialValues = React.useMemo(
    (): TemplateValues => ({
      title: template.title,
      titleEn: template.titleEn ?? '',
      status: String(template.status),
    }),
    [template]
  )

  const statusOptions = React.useMemo(
    () => [
      { value: '1', label: t('common:active') },
      { value: '0', label: t('common:inactive') },
    ],
    [t]
  )

  // A passive template without layouts cannot be activated: the 422 message is shown on the form as it comes
  const handleSubmit = React.useCallback(
    async (values: TemplateValues) => {
      await Api.putAdministratorStatementTemplate({
        headers,
        args: { id: template.id },
        body: { title: values.title.trim(), titleEn: values.titleEn.trim(), status: parseInt(values.status, 10) },
      })
      toast.success(t('statement:saved'))
      navigate(listPath)
    },
    [headers, template.id, t, navigate, listPath]
  )

  const handleCancel = React.useCallback(() => {
    navigate(listPath)
  }, [navigate, listPath])

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      <div className="flex flex-col gap-2">
        <TextInput type="text" name="title" label={t('common:name')} required disabled={!canManage} />
        <TextInput type="text" name="titleEn" label={t('common:name_en')} disabled={!canManage} />
        <SelectInput label={t('common:status')} name="status" options={statusOptions} required disabled={!canManage} />
        <FormError />
      </div>
      <div className="flex w-full justify-end gap-2 border-t border-borderGrey py-4">
        <Button type="button" variant="yellow" onClick={handleCancel}>
          {t('common:cancel')}
        </Button>
        {canManage && <FormSubmit label={t('common:save')} />}
      </div>
    </Form>
  )
}
